// src/hooks/useFichajes.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    startOfWeek, endOfWeek, startOfMonth, endOfMonth,
    startOfDay, endOfDay, isWithinInterval, differenceInMinutes
} from 'date-fns';
import api from '../services/api';
import socket from '../services/socket'; // 🚀 IMPORTAMOS EL SOCKET
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

// Duración de un fichaje en minutos. Si sigue abierto, cuenta hasta "ahora".
const minutosTrabajados = (fichaje, ahora) => {
    if (!fichaje.entrada) return 0;
    const inicio = new Date(fichaje.entrada);
    const fin = fichaje.salida ? new Date(fichaje.salida) : ahora;
    const mins = differenceInMinutes(fin, inicio);
    return mins > 0 ? mins : 0;
};

export function useFichajes() {
    const { user } = useAuth();
    const { showToast } = useToast();

    const [fichajesOriginales, setFichajesOriginales] = useState([]);
    const [profesionales, setProfesionales] = useState([]); // 👥 plantilla completa
    const [loading, setLoading] = useState(true);

    const [fechaFiltro, setFechaFiltro] = useState(new Date().toISOString().split('T')[0]);
    const [medicoFiltro, setMedicoFiltro] = useState('todos');

    const userRole = user?.role || user?.rol || 'medico';
    const isGlobalAdmin = ['supermedico', 'recepcion', 'admin'].includes(userRole);

    const cargarFichajes = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/fichajes/reportes');
            const payload = res?.data || res;
            let datosBrutos = Array.isArray(payload) ? payload : (payload?.docs || payload?.data || []);

            let datosFichajes = datosBrutos.map(f => ({
                ...f,
                usuario: f.profesional || f.usuario || {}
            }));

            if (!isGlobalAdmin && user) {
                datosFichajes = datosFichajes.filter(f => {
                    const idFichaje = String(f.usuario?.id || f.usuario?._id || f.usuario);
                    const idLogueado = String(user.id || user._id);
                    return idFichaje === idLogueado;
                });
            }

            setFichajesOriginales(datosFichajes);
        } catch (err) {
            console.error('Error al cargar fichajes:', err);
            setFichajesOriginales([]);
        } finally {
            setLoading(false);
        }
    }, [user, isGlobalAdmin]);

    // 👥 Cargamos la plantilla COMPLETA de profesionales (como la Agenda), para que
    // el desplegable del monitor liste a todos, incluso a quien aún no ha fichado.
    const cargarProfesionales = useCallback(async () => {
        if (!isGlobalAdmin) return; // un médico normal solo se ve a sí mismo
        try {
            const res = await api.get('/profesionales');
            const lista = Array.isArray(res) ? res : (res?.data || []);
            setProfesionales(lista);
        } catch (err) {
            console.error('Error al cargar la plantilla de profesionales:', err);
        }
    }, [isGlobalAdmin]);

    // Carga inicial
    useEffect(() => {
        cargarFichajes();
        cargarProfesionales();
    }, [cargarFichajes, cargarProfesionales]);

    // Refs para que el listener del socket (montado una sola vez) acceda siempre
    // a la última versión de la lista y de la función de recarga, sin re-suscribirse.
    const fichajesRef = useRef([]);
    const cargarFichajesRef = useRef(cargarFichajes);
    useEffect(() => { fichajesRef.current = fichajesOriginales; }, [fichajesOriginales]);
    useEffect(() => { cargarFichajesRef.current = cargarFichajes; }, [cargarFichajes]);

    // 🚀 SINCRONIZACIÓN EN TIEMPO REAL CON SOCKETS
    useEffect(() => {
        if (!socket.connected) socket.connect();

        const handleFichajeActualizado = (fichajeModificado) => {
            const idModificado = String(fichajeModificado.id || fichajeModificado._id);
            const existe = fichajesRef.current.some(f => String(f.id || f._id) === idModificado);

            if (existe) {
                // Fichaje YA en la lista (ej. salida, cierre automático): actualización
                // en el sitio, conservando el 'usuario' poblado para no perder el nombre.
                setFichajesOriginales(prev =>
                    prev.map(f =>
                        String(f.id || f._id) === idModificado
                            ? { ...f, ...fichajeModificado, usuario: f.usuario }
                            : f
                    )
                );
            } else {
                // 🎯 FIX: fichaje NUEVO (una ENTRADA recién creada). El payload del
                // socket viene sin el profesional poblado ni la IP descifrada, así que
                // recargamos desde el backend para traerlo completo. Sin esto, una
                // entrada nueva no aparecía hasta refrescar la página manualmente.
                cargarFichajesRef.current();
            }
        };

        // 🔔 Alertas legales en vivo (descanso insuficiente / jornada excedida)
        const handleAlerta = (alerta) => {
            if (!isGlobalAdmin || !alerta?.mensaje) return;
            const nombre = alerta.nombre ? `${alerta.nombre}: ` : '';
            showToast(`${nombre}${alerta.mensaje}`, alerta.tipo === 'JORNADA_EXCEDIDA' ? 'error' : 'warning');
            cargarFichajesRef.current();
        };

        socket.on('fichaje_actualizado', handleFichajeActualizado);
        socket.on('alerta_fichaje', handleAlerta);

        return () => {
            socket.off('fichaje_actualizado', handleFichajeActualizado);
            socket.off('alerta_fichaje', handleAlerta);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGlobalAdmin]);

    const medicosDisponibles = useMemo(() => {
        const map = new Map();

        // 1) Fuente principal: la plantilla completa (incluye a quien no ha fichado).
        //    FIX: antes la lista salía solo de los fichajes, por eso un profesional
        //    sin ningún fichaje (p. ej. recién dado de alta) no aparecía aquí aunque
        //    sí saliera en la Agenda.
        profesionales.forEach(p => {
            const id = p.id || p._id;
            if (id) map.set(String(id), `${p.nombre || 'Profesional'} ${p.apellidos || ''}`.trim());
        });

        // 2) Complemento: cualquier profesional presente en fichajes que no estuviera
        //    en la plantilla (p. ej. ya desactivado pero con histórico de fichajes).
        fichajesOriginales.forEach(f => {
            const id = f.usuario?.id || f.usuario?._id;
            if (id && !map.has(String(id))) {
                map.set(String(id), `${f.usuario.nombre || 'Profesional'} ${f.usuario.apellidos || ''}`.trim());
            }
        });

        return Array.from(map.entries())
            .map(([id, nombre]) => ({ id, nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [profesionales, fichajesOriginales]);

    const fichajesFiltrados = useMemo(() => {
        return fichajesOriginales.filter(f => {
            const idFichaje = String(f.usuario?.id || f.usuario?._id || f.usuario);
            const pasaMedico = medicoFiltro === 'todos' || idFichaje === String(medicoFiltro);

            let pasaFecha = true;
            if (fechaFiltro && f.entrada) {
                const fechaEntrada = new Date(f.entrada).toISOString().split('T')[0];
                pasaFecha = fechaEntrada === fechaFiltro;
            }

            return pasaMedico && pasaFecha;
        });
    }, [fichajesOriginales, medicoFiltro, fechaFiltro]);

    // 📊 ESTADÍSTICAS DE JORNADA (calculadas sobre TODOS los fichajes cargados,
    // respetando el filtro de médico pero NO el de fecha, para dar contexto de
    // hoy / semana / mes). Para un médico no-admin, ya vienen filtrados a lo suyo.
    const estadisticas = useMemo(() => {
        const ahora = new Date();
        const rangos = {
            hoy: { start: startOfDay(ahora), end: endOfDay(ahora) },
            semana: { start: startOfWeek(ahora, { weekStartsOn: 1 }), end: endOfWeek(ahora, { weekStartsOn: 1 }) },
            mes: { start: startOfMonth(ahora), end: endOfMonth(ahora) }
        };

        const base = fichajesOriginales.filter(f => {
            const idFichaje = String(f.usuario?.id || f.usuario?._id || f.usuario);
            return medicoFiltro === 'todos' || idFichaje === String(medicoFiltro);
        });

        let minHoy = 0, minSemana = 0, minMes = 0, sesionesMes = 0;
        const activos = new Set();

        for (const f of base) {
            if (!f.entrada) continue;
            const entrada = new Date(f.entrada);
            const mins = minutosTrabajados(f, ahora);

            if (isWithinInterval(entrada, rangos.hoy)) minHoy += mins;
            if (isWithinInterval(entrada, rangos.semana)) minSemana += mins;
            if (isWithinInterval(entrada, rangos.mes)) { minMes += mins; sesionesMes++; }
            if (f.estado === 'abierto') {
                activos.add(String(f.usuario?.id || f.usuario?._id || f.usuario));
            }
        }

        return {
            minutosHoy: minHoy,
            minutosSemana: minSemana,
            minutosMes: minMes,
            sesionesMes,
            activosAhora: activos.size
        };
    }, [fichajesOriginales, medicoFiltro]);

    return {
        fichajes: fichajesFiltrados,
        loading,
        cargarFichajes,
        isGlobalAdmin,
        fechaFiltro, setFechaFiltro,
        medicoFiltro, setMedicoFiltro,
        medicosDisponibles,
        estadisticas
    };
}